import { blueprints, blueprintsByFdName, blueprintsByKey, experimentalEffectsByKey, materialsByKey } from "./data.ts";
import type {
  Blueprint,
  BlueprintGrade,
  ExperimentalEffect,
  Ingredient,
  Material,
  MaterialCategory,
} from "./types.ts";

export interface EngineeringPlanStep {
  grade: number;
  ingredients: Ingredient[];
  engineers: string[];
  effects: BlueprintGrade["effects"];
  ingredientsSource: BlueprintGrade["ingredientsSource"];
}

export interface PlanMaterialRequirement {
  materialKey: string | null;
  name: string;
  category: MaterialCategory | null;
  required: number;
  owned: number;
  missing: number;
  usedBy: string[];
}

export interface EngineeringPlanResult {
  blueprint: Blueprint;
  targetGrade: number;
  currentGrade: number;
  steps: EngineeringPlanStep[];
  experimental: ExperimentalEffect | null;
  engineersForTarget: string[];
  totals: PlanMaterialRequirement[];
  /** True when any recipe involved is not fully verified data. */
  hasUnverifiedData: boolean;
}

export class EngineeringPlanError extends Error {
  public readonly code:
    | "unknown-blueprint"
    | "unknown-experimental-effect"
    | "grade-out-of-range"
    | "nothing-to-do";

  constructor(code: EngineeringPlanError["code"], message: string) {
    super(message);
    this.name = "EngineeringPlanError";
    this.code = code;
  }
}

export function getBlueprint(key: string): Blueprint | undefined {
  return blueprintsByKey.get(key);
}

export interface EngineerOffering {
  blueprint: Blueprint;
  /** Module display name (null for blueprints without a resolved module). */
  moduleName: string | null;
  /** Highest grade this engineer can apply for the blueprint. */
  maxGrade: number;
  /** All grades this engineer offers for the blueprint, ascending. */
  grades: number[];
}

/**
 * Every blueprint (with the grades this engineer can apply) offered by the
 * named engineer, derived from the per-grade engineer lists in the blueprint
 * dataset. Sorted by module name then blueprint name for stable UI ordering.
 */
export function blueprintsByEngineer(engineerName: string): EngineerOffering[] {
  const offerings: EngineerOffering[] = [];
  for (const blueprint of blueprints) {
    const grades = blueprint.grades
      .filter((grade) => grade.engineers.includes(engineerName))
      .map((grade) => grade.grade)
      .sort((a, b) => a - b);
    if (grades.length === 0) continue;
    offerings.push({
      blueprint,
      moduleName: blueprint.moduleName,
      maxGrade: grades[grades.length - 1] as number,
      grades,
    });
  }
  return offerings.sort((a, b) => {
    const aLabel = a.moduleName ?? a.blueprint.displayName;
    const bLabel = b.moduleName ?? b.blueprint.displayName;
    return aLabel.localeCompare(bLabel) || a.blueprint.name.localeCompare(b.blueprint.name);
  });
}

export function getBlueprintGrade(
  blueprint: Blueprint,
  grade: number,
): BlueprintGrade | undefined {
  return blueprint.grades.find((g) => g.grade === grade);
}

/**
 * Journal `EngineerCraft` events reference blueprints by fdname. Several
 * module variants can share an fdname (generic weapon blueprints), so an
 * optional coriolis module key narrows the match.
 */
export function findBlueprintForFdName(
  fdName: string,
  moduleKey?: string | null,
): Blueprint | undefined {
  const candidates = blueprintsByFdName.get(fdName);
  if (!candidates || candidates.length === 0) return undefined;
  if (moduleKey) {
    const narrowed = candidates.find((b) => b.moduleKeys.includes(moduleKey));
    if (narrowed) return narrowed;
  }
  return candidates[0];
}

/**
 * Material requirements for applying `targetGrade` to a module that is
 * currently at `currentGrade` (0 = unmodified).
 *
 * Game mechanics used here: reaching grade N requires one craft at every
 * grade from currentGrade+1 to N, each consuming that grade's recipe once.
 * Experimental effects consume their listed recipe once. Unknown recipes are
 * surfaced with `required: 0` shortfalls reported via `hasUnverifiedData`
 * instead of guessed numbers.
 */
export function calculateRequirements(
  input: {
    blueprintKey: string;
    targetGrade: number;
    currentGrade?: number;
    experimentalKey?: string | null;
  },
  owned: Record<string, number> = {},
): EngineeringPlanResult {
  const blueprint = blueprintsByKey.get(input.blueprintKey);
  if (!blueprint) {
    throw new EngineeringPlanError(
      "unknown-blueprint",
      `Blueprint "${input.blueprintKey}" is not in the EDCC game data.`,
    );
  }

  const currentGrade = input.currentGrade ?? 0;
  const targetGrade = input.targetGrade;
  if (currentGrade < 0 || currentGrade > 5 || targetGrade < 1 || targetGrade > 5) {
    throw new EngineeringPlanError("grade-out-of-range", "Grades must be between 1 and 5.");
  }
  if (targetGrade <= currentGrade) {
    throw new EngineeringPlanError(
      "nothing-to-do",
      `Target grade ${targetGrade} is not above current grade ${currentGrade}.`,
    );
  }

  const steps: EngineeringPlanStep[] = [];
  let hasUnverifiedData = false;
  const stepsInRange = blueprint.grades.filter(
    (g) => g.grade > currentGrade && g.grade <= targetGrade,
  );
  for (const grade of stepsInRange) {
    if (!grade.ingredients) {
      hasUnverifiedData = true;
      continue;
    }
    steps.push({
      grade: grade.grade,
      ingredients: grade.ingredients,
      engineers: grade.engineers,
      effects: grade.effects,
      ingredientsSource: grade.ingredientsSource,
    });
    if (grade.ingredientsSource !== "uuid" && grade.ingredientsSource !== "coriolis-only") {
      hasUnverifiedData = true;
    }
  }

  let experimental: ExperimentalEffect | null = null;
  if (input.experimentalKey) {
    experimental = experimentalEffectsByKey.get(input.experimentalKey) ?? null;
    if (!experimental) {
      throw new EngineeringPlanError(
        "unknown-experimental-effect",
        `Experimental effect "${input.experimentalKey}" is not in the EDCC game data.`,
      );
    }
  }

  const totals = new Map<string, PlanMaterialRequirement>();
  const addRequirement = (ingredient: Ingredient, usedBy: string) => {
    const material: Material | undefined = ingredient.materialKey
      ? materialsByKey.get(ingredient.materialKey)
      : undefined;
    const mapKey = ingredient.materialKey ?? `unknown:${ingredient.name}`;
    const existing = totals.get(mapKey) ?? {
      materialKey: ingredient.materialKey,
      name: material?.name ?? ingredient.name,
      category: material?.category ?? null,
      required: 0,
      owned: ingredient.materialKey ? (owned[ingredient.materialKey] ?? 0) : 0,
      missing: 0,
      usedBy: [],
    };
    existing.required += ingredient.size;
    existing.missing = Math.max(0, existing.required - existing.owned);
    if (!existing.usedBy.includes(usedBy)) existing.usedBy.push(usedBy);
    totals.set(mapKey, existing);
  };

  for (const step of steps) {
    for (const ingredient of step.ingredients) {
      addRequirement(ingredient, `Grade ${step.grade}`);
    }
  }
  if (experimental) {
    for (const ingredient of experimental.ingredients) {
      addRequirement(ingredient, `Experimental: ${experimental.name}`);
    }
  }

  const targetGradeData = getBlueprintGrade(blueprint, targetGrade);
  return {
    blueprint,
    targetGrade,
    currentGrade,
    steps,
    experimental,
    engineersForTarget: targetGradeData?.engineers ?? [],
    totals: [...totals.values()].sort((a, b) => a.name.localeCompare(b.name)),
    hasUnverifiedData,
  };
}
